import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Save, Upload, Image as ImageIcon, Settings, FileText, Layout, Plus, Edit2 } from 'lucide-react';
import BackButton from '../components/BackButton';
import { getToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContentManagement = () => {
  const _navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPage, setSelectedPage] = useState('home');
  const [pageContent, setPageContent] = useState({});
  const [_pages, _setPages] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [activeKey, setActiveKey] = useState('');

  // Configuration de l'éditeur Quill
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align',
    'link', 'image'
  ];

  // Pages disponibles
  const availablePages = [
    { id: 'home', name: 'Accueil O\'SCOP', icon: '🏠' },
    { id: 'dispositifs_btp', name: 'Activités du BTP', icon: '🏗️' },
    { id: 'oscop_ge', name: 'O\'SCOP GE', icon: '👥' },
    { id: 'formascop', name: 'FORMA\'SCOP', icon: '🎓' },
    { id: 'logistique_mobilite', name: 'Logistique & Mobilité', icon: '🚚' },
    { id: 'agro_vegetal', name: 'AGRO & Végétal', icon: '🌱' },
    { id: 'batiment_expertise', name: 'Bâtiment & Expertise', icon: '🏢' },
    { id: 'header', name: 'Header (Global)', icon: '📋' },
    { id: 'footer', name: 'Footer (Global)', icon: '📄' }
  ];

  useEffect(() => {
    loadPageContent();
    loadUploadedImages();
  }, [selectedPage]);

  const loadPageContent = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/cms/content/${selectedPage}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPageContent(response.data.sections || {});
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const loadUploadedImages = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/cms/images`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUploadedImages(response.data.images || []);
    } catch {
    }
  };

  const handleContentChange = (section, key, value) => {
    setPageContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: {
          ...prev[section]?.[key],
          value: value
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = getToken();
      
      // Préparer les mises à jour
      const updates = [];
      for (const [section, content] of Object.entries(pageContent)) {
        for (const [key, data] of Object.entries(content)) {
          updates.push({
            page: selectedPage,
            section,
            key,
            value: data.value,
            type: data.type || 'text'
          });
        }
      }

      await axios.post(
        `${BACKEND_URL}/api/admin/cms/content/bulk`,
        { updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Contenu sauvegardé avec succès !');
    } catch {
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = getToken();
      const _response = await axios.post(
        `${BACKEND_URL}/api/admin/cms/upload/image`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      alert('✅ Image uploadée avec succès !');
      loadUploadedImages();
    } catch {
      alert('❌ Erreur lors de l\'upload');
    }
  };

  const handleInitializeContent = async () => {
    if (!window.confirm('Initialiser le contenu par défaut ? Cela créera des contenus de base pour toutes les pages.')) {
      return;
    }

    try {
      const token = getToken();
      await axios.post(
        `${BACKEND_URL}/api/admin/cms/pages/initialize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Contenu initialisé avec succès !');
      loadPageContent();
    } catch {
      alert('❌ Erreur lors de l\'initialisation');
    }
  };

  const insertImageIntoEditor = (imageUrl) => {
    if (activeSection && activeKey) {
      handleContentChange(activeSection, activeKey, 
        (pageContent[activeSection]?.[activeKey]?.value || '') + `<img src="${imageUrl}" alt="Image" style="max-width: 100%;" />`
      );
      setShowImageGallery(false);
    }
  };

  const renderContentEditor = (section, key, data) => {
    const type = data?.type || 'text';
    const value = data?.value || '';

    switch (type) {
      case 'rich_text':
        return (
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {key.replace(/_/g, ' ').toUpperCase()}
            </label>
            <ReactQuill
              theme="snow"
              value={value}
              onChange={(content) => handleContentChange(section, key, content)}
              modules={quillModules}
              formats={quillFormats}
              className="bg-white rounded-lg"
              onFocus={() => {
                setActiveSection(section);
                setActiveKey(key);
              }}
            />
          </div>
        );
      
      case 'image':
        return (
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {key.replace(/_/g, ' ').toUpperCase()}
            </label>
            <div className="flex items-center gap-4">
              <input
                type="text"
                value={value}
                onChange={(e) => handleContentChange(section, key, e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="URL de l'image"
              />
              <Button
                onClick={() => {
                  setActiveSection(section);
                  setActiveKey(key);
                  setShowImageGallery(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Galerie
              </Button>
            </div>
            {value && (
              <div className="mt-2">
                <img 
                  src={value.startsWith('http') ? value : `${BACKEND_URL}${value}`} 
                  alt="Aperçu" 
                  className="h-32 w-auto rounded border"
                />
              </div>
            )}
          </div>
        );
      
      case 'text':
      default:
        return (
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {key.replace(/_/g, ' ').toUpperCase()}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleContentChange(section, key, e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="mb-2">
              <BackButton to="/admin/dashboard" label="Retour" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              Gestion du Contenu (CMS)
            </h1>
            <p className="text-gray-600 mt-2">
              Modifiez le contenu de toutes les pages du site
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={handleInitializeContent}
              variant="outline"
              className="border-blue-600 text-blue-600"
            >
              <Settings className="h-4 w-4 mr-2" />
              Initialiser
            </Button>
            
            <Button
              onClick={() => setShowImageGallery(true)}
              variant="outline"
              className="border-green-600 text-green-600"
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              Galerie
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Sélection de page */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layout className="h-5 w-5" />
                  Pages
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {availablePages.map(page => (
                  <button
                    key={page.id}
                    onClick={() => setSelectedPage(page.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      selectedPage === page.id
                        ? 'bg-blue-700 text-white font-semibold'
                        : 'hover:bg-gray-600 text-white'
                    }`}
                  >
                    <span className="mr-2">{page.icon}</span>
                    {page.name}
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Éditeur de contenu */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Edit2 className="h-5 w-5" />
                  Édition - {availablePages.find(p => p.id === selectedPage)?.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Chargement...</p>
                  </div>
                ) : Object.keys(pageContent).length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">Aucun contenu pour cette page</p>
                    <Button
                      onClick={handleInitializeContent}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Initialiser le contenu
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(pageContent).map(([section, content]) => (
                      <div key={section} className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <span className="text-blue-600">📝</span>
                          {section.replace(/_/g, ' ').toUpperCase()}
                        </h3>
                        
                        {Object.entries(content).map(([key, data]) => (
                          <div key={key}>
                            {renderContentEditor(section, key, data)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Modal Galerie d'images */}
        {showImageGallery && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-4xl w-full max-h-[80vh] overflow-auto">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Galerie d'Images</span>
                  <Button
                    onClick={() => setShowImageGallery(false)}
                    variant="ghost"
                  >
                    ✕
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <div className="border-2 border-dashed border-blue-400 rounded-lg p-8 text-center hover:bg-blue-50 transition-colors">
                      <Upload className="h-12 w-12 text-blue-600 mx-auto mb-2" />
                      <p className="text-blue-600 font-semibold">Cliquez pour uploader une image</p>
                      <p className="text-gray-500 text-sm">JPG, PNG, GIF, WEBP (max 5MB)</p>
                    </div>
                  </label>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {uploadedImages.map((img, index) => (
                    <div
                      key={index}
                      onClick={() => insertImageIntoEditor(img.url)}
                      className="cursor-pointer border-2 border-gray-200 rounded-lg p-2 hover:border-blue-500 transition-colors"
                    >
                      <img
                        src={`${BACKEND_URL}${img.url}`}
                        alt={img.filename}
                        className="w-full h-32 object-cover rounded"
                      />
                      <p className="text-xs text-gray-600 mt-2 truncate">{img.filename}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContentManagement;
